/**
 * MountedContextPanel — read-only rollup of every Data Plane asset visible
 * to a network. Replaces the folder-tree mental model in "Run -> Input Data"
 * with a flat, grouped view that mirrors the spec:
 *
 *     Org Context        : 1 file
 *     Network Context    : 2 files
 *     Pool Context       : 0 files
 *     Agent Context      : 1 file
 *     Agent + Pool       : 0 files
 *
 * Each row is a chip that links back to the Data Plane catalog ("Manage in
 * Data Plane"), so users always know **where** the asset lives — nothing is
 * hidden in unlabeled S3 paths anymore.
 *
 * Wire shape:
 *
 *   listNetworkMounts(networkId, { includeOrg: true, scope?, pool?, agent? })
 *     → { networkMounts: Mount[], orgMounts: Mount[] }
 *
 * The rollup is **read-only**; mounts are managed in the Data Plane tab.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Database, ExternalLink, FileText, RefreshCw } from 'lucide-react';

import { listNetworkMounts } from '../../services/dataPlaneApi';
import MountChip from './MountChip.jsx';

// Order matters — we render groups top-to-bottom in this exact sequence so
// the UX is stable across networks and matches the spec verbatim.
const GROUP_ORDER = ['ORG', 'NETWORK', 'POOL', 'AGENT', 'AGENT_POOL'];
const GROUP_TITLE = {
  ORG: 'Org Context',
  NETWORK: 'Network Context',
  POOL: 'Pool Context',
  AGENT: 'Agent Context',
  AGENT_POOL: 'Agent + Pool Context',
};
const GROUP_HINT = {
  ORG: 'Workspace-wide assets, available to every network in this tenant.',
  NETWORK: 'Mounted to this network — visible to every agent and pool.',
  POOL: 'Mounted to a specific runtime customer (pool) of this network.',
  AGENT: 'Mounted to a single agent in this network.',
  AGENT_POOL: 'Mounted to one agent for one pool — most narrowly scoped.',
};

function groupMounts(networkMounts, orgMounts) {
  const buckets = { ORG: [], NETWORK: [], POOL: [], AGENT: [], AGENT_POOL: [] };
  for (const m of orgMounts || []) buckets.ORG.push(m);
  for (const m of networkMounts || []) {
    const scope = (m && m.target && m.target.scope) || '';
    if (buckets[scope]) buckets[scope].push(m);
  }
  return buckets;
}

function formatBytes(n) {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / 1024 / 1024).toFixed(1)} MB`;
  return `${(v / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function MountRow({ mount }) {
  const asset = mount.asset || {};
  return (
    <div
      className="flex items-center gap-2 py-1.5 px-2 hover:bg-gray-50 rounded text-xs"
      data-testid="mounted-context-row"
    >
      <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="font-medium text-gray-800 truncate" title={asset.fileName || mount.assetId}>
          {asset.fileName || mount.assetId}
        </div>
        {asset.usageType ? (
          <div className="text-[10px] uppercase tracking-wide text-gray-500">
            {asset.usageType}
            {asset.sizeBytes ? <span className="ml-2 normal-case">{formatBytes(asset.sizeBytes)}</span> : null}
          </div>
        ) : null}
      </div>
      <MountChip target={mount.target} />
    </div>
  );
}

export default function MountedContextPanel({
  networkId,
  onManageInDataPlane,
  scopeFilter = null,
  poolFilter = null,
  agentFilter = null,
  refreshSeed = 0,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [networkMounts, setNetworkMounts] = useState([]);
  const [orgMounts, setOrgMounts] = useState([]);

  const load = useCallback(async () => {
    if (!networkId) {
      setNetworkMounts([]);
      setOrgMounts([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const opts = { includeOrg: true };
      if (scopeFilter) opts.scope = scopeFilter;
      if (poolFilter) opts.pool = poolFilter;
      if (agentFilter) opts.agent = agentFilter;
      const data = await listNetworkMounts(networkId, opts);
      setNetworkMounts(data.networkMounts || []);
      setOrgMounts(data.orgMounts || []);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [networkId, scopeFilter, poolFilter, agentFilter, refreshSeed]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => groupMounts(networkMounts, orgMounts), [networkMounts, orgMounts]);
  const totalCount = (networkMounts?.length || 0) + (orgMounts?.length || 0);

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="mounted-context-panel">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <Database className="w-3.5 h-3.5" aria-hidden="true" />
          <span>
            {totalCount} mounted {totalCount === 1 ? 'asset' : 'assets'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            aria-label="Refresh mounted context"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            Refresh
          </button>
          {onManageInDataPlane ? (
            <button
              type="button"
              onClick={onManageInDataPlane}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded text-blue-700 hover:bg-blue-50"
              data-testid="mounted-context-manage"
            >
              <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
              Manage in Context
            </button>
          ) : null}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto px-2 py-2 space-y-3">
        {error ? (
          <div className="text-xs text-red-600 px-2 py-2 rounded bg-red-50">
            Failed to load mounted context: {error}
          </div>
        ) : null}

        {!error && totalCount === 0 && !loading ? (
          <div className="text-xs text-gray-500 px-2 py-3" data-testid="mounted-context-empty">
            No assets mounted to this network yet.{' '}
            {onManageInDataPlane ? (
              <button
                type="button"
                onClick={onManageInDataPlane}
                className="text-blue-700 hover:underline"
              >
                Open Context
              </button>
            ) : null}{' '}
            to upload and mount RULES / REFERENCE assets.
          </div>
        ) : null}

        {GROUP_ORDER.map((group) => {
          const items = groups[group] || [];
          if (items.length === 0) return null;
          return (
            <section key={group} data-testid={`mounted-context-group-${group}`}>
              <header className="px-2 mb-1">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                  {GROUP_TITLE[group]} <span className="text-gray-400 font-normal">({items.length})</span>
                </div>
                <div className="text-[10px] text-gray-500">{GROUP_HINT[group]}</div>
              </header>
              <div>
                {items.map((m) => (
                  <MountRow key={m.mountId || `${m.assetId}::${JSON.stringify(m.target)}`} mount={m} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
